import { createSlice } from "@reduxjs/toolkit";
import { format, parseISO, subMonths, subWeeks } from "date-fns";
import { hideLoading, showLoading } from "react-redux-loading-bar";
import { getEngagementSummary, getEngagements, retryEngagementFollowUp } from "../../../api/engagementAPI";

export const ENGAGEMENT_STAGES = [
  "NEW_LEAD_IN",
  "FIRST_CONTACT_MADE",
  "COLD_LEAD",
  "CLOSED_WON",
];

const previousDateTime = ({ viewBy, dateTime }) => viewBy === "month"
  ? format(subMonths(parseISO(dateTime), 1), "yyyy-MM")
  : format(subWeeks(parseISO(dateTime), 1), "yyyy-MM-dd");

const initialPeriod = { viewBy: "month", dateTime: format(new Date(), "yyyy-MM") };

const initialState = {
  summary: {},
  previousSummary: {},
  comparisonStatus: "idle",
  engagements: [],
  pagination: {},
  page: 0,
  rowsPerPage: 10,
  selectedStage: ENGAGEMENT_STAGES[0],
  summaryStatus: "idle",
  listStatus: "idle",
  errorMessage: "",
  retryingIds: [],
  period: initialPeriod,
  comparisonPeriod: { viewBy: initialPeriod.viewBy, dateTime: previousDateTime(initialPeriod) },
};

export const engagementSlice = createSlice({
  name: "engagement",
  initialState,
  reducers: {
    setRetrying: (state, action) => {
      const { id, pending } = action.payload;
      state.retryingIds = state.retryingIds.filter((value) => value !== id);
      if (pending) state.retryingIds.push(id);
    },
    setPeriod: (state, action) => {
      state.period = action.payload;
      state.comparisonPeriod = {
        viewBy: action.payload.viewBy,
        dateTime: previousDateTime(action.payload),
      };
      state.page = 0;
      state.summary = {};
      state.previousSummary = {};
      state.comparisonStatus = "idle";
      state.engagements = [];
      state.pagination = {};
      state.errorMessage = "";
    },
    setComparisonPeriod: (state, action) => {
      state.comparisonPeriod = action.payload;
      state.previousSummary = {};
      state.comparisonStatus = "idle";
    },
    setSummary: (state, action) => {
      state.summary = action.payload;
      state.summaryStatus = "success";
    },
    setPreviousSummary: (state, action) => {
      state.previousSummary = action.payload;
      state.comparisonStatus = "success";
    },
    setComparisonStatus: (state, action) => {
      state.comparisonStatus = action.payload;
    },
    setEngagements: (state, action) => {
      state.engagements = action.payload;
      state.listStatus = "success";
    },
    setPagination: (state, action) => {
      state.pagination = action.payload;
    },
    setPage: (state, action) => {
      state.page = action.payload;
    },
    setRowsPerPage: (state, action) => {
      state.rowsPerPage = action.payload;
    },
    setSelectedStage: (state, action) => {
      state.selectedStage = action.payload;
    },
    setSummaryStatus: (state, action) => {
      state.summaryStatus = action.payload;
    },
    setListStatus: (state, action) => {
      state.listStatus = action.payload;
    },
    setErrorMessage: (state, action) => {
      state.errorMessage = action.payload;
    },
  },
});

export const {
  setRetrying,
  setPeriod,
  setComparisonPeriod,
  setPreviousSummary,
  setComparisonStatus,
  setSummary,
  setEngagements,
  setPagination,
  setPage,
  setRowsPerPage,
  setSelectedStage,
  setSummaryStatus,
  setListStatus,
  setErrorMessage,
} = engagementSlice.actions;

export const selectState = (state) => state.engagement;

export const handleRetryFollowUp = (engagement) => async (dispatch, getState) => {
  if (getState().engagement.retryingIds.includes(engagement.id)) return;
  dispatch(setRetrying({ id: engagement.id, pending: true }));
  try {
    await retryEngagementFollowUp(engagement.id, engagement.version);
  } catch (error) {
    dispatch(setErrorMessage(error.response?.status === 409
      ? "This engagement changed. The list has been refreshed; review it before retrying."
      : "Could not resume follow-up. Please try again."));
  } finally {
    const current = getState().engagement;
    await dispatch(handleGetEngagements(current.selectedStage));
    dispatch(setRetrying({ id: engagement.id, pending: false }));
  }
};

const unwrapPayload = (payload) => payload?.data ?? payload;

const extractEngagementPage = (payload) => {
  const unwrapped = unwrapPayload(payload);
  if (Array.isArray(unwrapped)) {
    return {
      engagements: unwrapped,
      pagination: { totalElements: unwrapped.length },
    };
  }

  const { content, items, ...pagination } = unwrapped ?? {};
  return {
    engagements: Array.isArray(content) ? content : Array.isArray(items) ? items : [],
    pagination,
  };
};

export const handleGetComparisonSummary = () => async (dispatch, getState) => {
  const { period, comparisonPeriod } = getState().engagement;
  dispatch(setComparisonStatus("loading"));
  try {
    const response = await getEngagementSummary(comparisonPeriod);
    const latest = getState().engagement;
    if (latest.period === period && latest.comparisonPeriod === comparisonPeriod) {
      dispatch(setPreviousSummary(unwrapPayload(response.data) ?? {}));
    }
  } catch (error) {
    const latest = getState().engagement;
    if (latest.period === period && latest.comparisonPeriod === comparisonPeriod) {
      dispatch(setComparisonStatus("error"));
    }
  }
};

export const handleGetEngagementSummary = () => async (dispatch, getState) => {
  const period = getState().engagement.period;
  dispatch(showLoading());
  dispatch(setSummaryStatus("loading"));

  try {
    const [current] = await Promise.allSettled([
      getEngagementSummary(period),
      dispatch(handleGetComparisonSummary()),
    ]);
    if (getState().engagement.period === period) {
      if (current.status === "fulfilled") {
        dispatch(setSummary(unwrapPayload(current.value.data) ?? {}));
      } else {
        dispatch(setSummaryStatus("error"));
        dispatch(setErrorMessage(current.reason.message));
      }
      if (current.status !== "fulfilled") {
        dispatch(setComparisonStatus("error"));
      }
    }
  } catch (error) {
    if (getState().engagement.period === period) {
      dispatch(setSummaryStatus("error"));
      dispatch(setComparisonStatus("error"));
      dispatch(setErrorMessage(error.message));
    }
  } finally {
    dispatch(hideLoading());
  }
};

export const handleGetEngagements = (stage, page, rowsPerPage) => async (dispatch, getState) => {
  const currentState = getState().engagement;
  const period = currentState.period;
  const requestedPage = page ?? currentState.page;
  const requestedRowsPerPage = rowsPerPage ?? currentState.rowsPerPage;

  dispatch(showLoading());
  dispatch(setSelectedStage(stage));
  dispatch(setPage(requestedPage));
  dispatch(setRowsPerPage(requestedRowsPerPage));
  dispatch(setListStatus("loading"));

  try {
    const response = await getEngagements(stage, requestedPage, requestedRowsPerPage, period);
    const latestState = getState().engagement;
    if (
      latestState.period === period &&
      latestState.selectedStage === stage &&
      latestState.page === requestedPage &&
      latestState.rowsPerPage === requestedRowsPerPage
    ) {
      const { engagements, pagination } = extractEngagementPage(response.data);
      dispatch(setPagination(pagination));
      dispatch(setEngagements(engagements));
    }
  } catch (error) {
    const latestState = getState().engagement;
    if (
      latestState.period === period &&
      latestState.selectedStage === stage &&
      latestState.page === requestedPage &&
      latestState.rowsPerPage === requestedRowsPerPage
    ) {
      dispatch(setPagination({}));
      dispatch(setEngagements([]));
      dispatch(setListStatus("error"));
      dispatch(setErrorMessage(error.message));
    }
  } finally {
    dispatch(hideLoading());
  }
};

export default engagementSlice.reducer;
